/**
 * useEmbroideryMapping
 *
 * Everything that happens to an embroidery artwork after it is on the canvas:
 * which object it is, what "Apply" does to it, how it is removed, and keeping
 * the "there is artwork here" flag honest.
 *
 * Extracted from CanvasEditor, which was already long and had no test harness —
 * so the confirm sequence, the part that produces the file customers receive,
 * could not be exercised at all. A hook can be rendered on its own.
 */

import { useCallback, useEffect, useMemo } from 'react';
import type { MutableRefObject } from 'react';
import { Canvas as FabricCanvas, FabricImage } from 'fabric';
import toast from 'react-hot-toast';
import { MAX_UPLOAD_BYTES, THREAD_COLORS } from '@designdeck/shared';
import type { PrintContext } from '@designdeck/shared';
import { uploadAsset } from '@/api/assets';
import { useDesignStore } from '@/stores';
import { useEmbroideryStore } from '@/stores/embroideryStore';
import {
  renderEmbroideryPreview,
  printResolutionEdge,
  canvasToBlob,
  PREVIEW_EDGE,
} from '@/lib/embroidery/renderPreview';
import { proxyStorageUrl } from '@/lib/product-transformer';
import { loadImage, logger, setAssetProperties } from '@/utils';
import { describeApiError } from '@/lib/api-errors';
import i18n from '@/lib/i18n';

/** Assets API refuses anything larger, and retrying a refusal is pointless. */
const MAX_RENDER_BYTES = MAX_UPLOAD_BYTES;

/**
 * Image loads carry no deadline of their own. The modal holds its busy state
 * for the whole confirm and disables every way out while busy, so a stalled
 * storage host would leave the seller with nothing but a reload.
 */
const LOAD_TIMEOUT_MS = 60_000;

function withTimeout<T>(promise: Promise<T>, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), LOAD_TIMEOUT_MS);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

export interface UseEmbroideryMappingParams {
  fabricRef: MutableRefObject<FabricCanvas | null>;
  activePrintContext: PrintContext | null;
  saveToHistory: (label: string) => void;
  /** Gates the canvas listeners until the canvas exists. */
  isInitialized: boolean;
}

export function useEmbroideryMapping({
  fabricRef,
  activePrintContext,
  saveToHistory,
  isInitialized,
}: UseEmbroideryMappingParams) {
  const isEmbroideryProduct = useEmbroideryStore((s) => s.isEmbroideryProduct);
  const setEmbroideryActive = useEmbroideryStore((s) => s.setEmbroideryActive);
  const clearEmbroidery = useEmbroideryStore((s) => s.clearEmbroidery);
  const closeEmbroideryModal = useEmbroideryStore((s) => s.closeModal);
  const commitEmbroideryDraft = useEmbroideryStore((s) => s.commitDraft);
  const isModalOpen = useEmbroideryStore((s) => s.isModalOpen);
  const embroideryMaxColors = useEmbroideryStore((s) => s.maxColors);
  const embroiderySource = useEmbroideryStore((s) => s.source);
  const embroideryPalette = useEmbroideryStore((s) => s.palette);

  /** The single embroidery motif on the canvas, if there is one. */
  const findEmbroideryObject = useCallback((): FabricImage | null => {
    const canvas = fabricRef.current;
    if (!canvas) return null;
    const found = canvas
      .getObjects()
      .find((obj) => typeof obj.embFileKey === 'string');
    return (found as FabricImage | undefined) ?? null;
  }, [fabricRef]);

  const embroiderySourceUrl = useMemo(() => {
    if (embroiderySource.objectUrl) return embroiderySource.objectUrl;
    return proxyStorageUrl(embroiderySource.embFileKey);
  }, [embroiderySource]);

  const handleEmbroideryConfirm = useCallback(
    async (mapping: Record<string, string>) => {
      const canvas = fabricRef.current;
      const target = findEmbroideryObject();
      if (!canvas || !target || !embroiderySourceUrl) return;

      // The store describes one artwork; the canvas may hold another by now —
      // a product switch while a reopen's analysis was still in flight. Going
      // on would render that artwork's pixels into this object and record its
      // mapping here: a wrong production file that reads as confirmed.
      const sourceKey = useEmbroideryStore.getState().source.embFileKey;
      if (sourceKey && target.embFileKey !== sourceKey) {
        toast.error(i18n.t('creator.toast.threadsForOtherArtwork'));
        closeEmbroideryModal();
        return;
      }

      try {
        const image = await withTimeout(
          loadImage(embroiderySourceUrl),
          i18n.t('creator.toast.artworkLoadTimeout')
        );

        // Size the shipped render from the motif's placed size, not the print
        // area — a small logo in a large area would otherwise be rendered by the
        // ratio between the two.
        // No silent fallback here. This scale converts the placed size into
        // millimetres and so decides the resolution of the file that ships;
        // defaulting to 1 px/mm during a product switch would size the artwork
        // by roughly two orders of magnitude and mark it confirmed.
        const pxPerMm = activePrintContext?.pxPerMm;
        if (!pxPerMm) {
          throw new Error('The print area is not ready yet — try again');
        }

        const maxEdge = printResolutionEdge({
          widthMm: target.getScaledWidth() / pxPerMm,
          heightMm: target.getScaledHeight() / pxPerMm,
          minDpi: useEmbroideryStore.getState().minDpi,
          sourceEdge: Math.max(image.naturalWidth, image.naturalHeight),
        });

        const render = (edge: number) =>
          renderEmbroideryPreview({
            image,
            palette: embroideryPalette,
            mapping,
            threads: THREAD_COLORS,
            maxEdge: edge,
          });

        const rendered = render(maxEdge);
        if (rendered.degraded) {
          // The canvas does not show the mapping — a tainted source, or no
          // thread layer painted at all. Uploading it would confirm a motif
          // nobody mapped as the production artwork.
          throw new Error(
            'The stitch preview could not be produced from this motif'
          );
        }

        let blob = await canvasToBlob(rendered.canvas);
        if (blob.size > MAX_RENDER_BYTES) {
          // Dense hatching encodes badly. Retrying at the interactive edge keeps
          // the upload inside the asset limit instead of letting uploadAsset
          // burn three attempts on a body the server will always refuse.
          blob = await canvasToBlob(render(PREVIEW_EDGE).canvas);
          if (blob.size > MAX_RENDER_BYTES) {
            throw new Error('The rendered artwork is too large to store');
          }
        }

        const asset = await uploadAsset(
          new File([blob], 'embroidery-render.png', { type: 'image/png' })
        );

        // Swap the element rather than calling setSrc: the established pattern
        // here, because setSrc can lose crossOrigin and taint the canvas. Unlike
        // the plain-image path the dimensions do change, so width/height and the
        // scale factors have to be re-derived or the motif jumps in size.
        const displayedWidth = target.getScaledWidth();
        const displayedHeight = target.getScaledHeight();
        const loaded = await withTimeout(
          FabricImage.fromURL(asset.url, { crossOrigin: 'anonymous' }),
          i18n.t('creator.toast.renderedArtworkLoadTimeout')
        );
        const element = loaded._element as HTMLImageElement | undefined;
        if (!element) throw new Error('Could not load the rendered artwork');
        element.crossOrigin = 'anonymous';

        target._element = element;
        (target as unknown as { src?: string }).src = asset.url;
        (target as unknown as { crossOrigin?: string }).crossOrigin =
          'anonymous';
        target.set({
          width: element.naturalWidth,
          height: element.naturalHeight,
          scaleX: displayedWidth / (element.naturalWidth || 1),
          scaleY: displayedHeight / (element.naturalHeight || 1),
        });
        target.setCoords();

        target.confirmedMapping = { ...mapping };
        setAssetProperties(target, {
          assetId: asset.id,
          assetType: 's3',
          _uploadStatus: 'completed',
        });

        canvas.requestRenderAll();
        saveToHistory('embroidery-threads');
        // Direct property writes fire no object:modified, so nothing else
        // marks the design dirty — and every save path (step navigation,
        // autosave, beforeunload) is gated on that flag. Without it the
        // confirmation never reaches masterPositions, and the Publish step
        // judges the design by the state before Apply.
        useDesignStore.getState().setHasUnsavedChanges(true);
        commitEmbroideryDraft();
      } catch (error) {
        // Leave the modal open and the motif unconfirmed: publishing stays
        // blocked, and the mapping the seller just built is not thrown away.
        const msg = describeApiError(error);
        logger.error('CanvasEditor', 'Embroidery apply failed:', msg);
        toast.error(
          i18n.t('creator.toast.threadsApplyFailed', { message: msg })
        );
      }
    },
    [
      fabricRef,
      findEmbroideryObject,
      embroiderySourceUrl,
      embroideryPalette,
      activePrintContext,
      saveToHistory,
      closeEmbroideryModal,
      commitEmbroideryDraft,
    ]
  );

  const handleRemoveEmbroideryMotif = useCallback(() => {
    const canvas = fabricRef.current;
    const target = findEmbroideryObject();
    if (canvas && target) {
      canvas.remove(target);
      canvas.requestRenderAll();
      saveToHistory('remove-embroidery');
    }
    clearEmbroidery();
  }, [fabricRef, findEmbroideryObject, saveToHistory, clearEmbroidery]);

  // A confirmed mapping is only valid against the ceiling it was confirmed
  // under. Design is step one and Products is step two, so activating a
  // stricter product after confirming is the normal path, not an edge case —
  // and the publish gate only asks "unconfirmed?", which a stale confirmation
  // answers with "no".
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isEmbroideryProduct) return;

    const motif = findEmbroideryObject();
    const mapping = motif?.confirmedMapping;
    if (!motif || !mapping) return;

    const threadCount = new Set(Object.values(mapping)).size;
    if (threadCount <= embroideryMaxColors) return;

    motif.confirmedMapping = undefined;
    canvas.requestRenderAll();
    useDesignStore.getState().setHasUnsavedChanges(true);
    toast.error(
      i18n.t('creator.toast.threadLimitExceeded', {
        max: embroideryMaxColors,
        count: threadCount,
      })
    );
  }, [
    fabricRef,
    embroideryMaxColors,
    isEmbroideryProduct,
    findEmbroideryObject,
  ]);

  // Keep the "a motif is on the canvas" flag tied to the canvas rather than to
  // the upload call. The store is wiped on every product switch, so a flag set
  // at upload time would go stale while the motif is still there — unlocking
  // the toolbar for a second image.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isEmbroideryProduct) return;
    const sync = () => setEmbroideryActive(!!findEmbroideryObject());
    sync();
    canvas.on('object:added', sync);
    canvas.on('object:removed', sync);
    return () => {
      canvas.off('object:added', sync);
      canvas.off('object:removed', sync);
    };
  }, [
    fabricRef,
    findEmbroideryObject,
    setEmbroideryActive,
    isEmbroideryProduct,
    isInitialized,
  ]);

  return {
    isModalOpen,
    sourceUrl: embroiderySourceUrl,
    confirm: handleEmbroideryConfirm,
    cancel: closeEmbroideryModal,
    removeArtwork: handleRemoveEmbroideryMotif,
  };
}
